import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { Spinner, Modal } from "react-bootstrap";
import { useAppDispatch } from "@/store";
import {
  fetchProductForShapeOfYou,
  selectShapeOfYou,
} from "@/store/slices/shapeOfYou";
import { useSelector } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Navigation, Thumbs, FreeMode } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css/free-mode";
import styles from "../../styles/shap_of_you.module.scss";
import jel from "../../assets/images/jel.png";
import madeInIndiaFlag from "../../assets/images/made-in-india.png";
import sglLogo from "../../assets/images/SGL.png";
import cartAddedIcon from "../../assets/svg/Icons.svg";

interface ProductDetailsModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedProduct: any;
  getImageSrc: (product: any) => string;
  cartItem: any;
  showAddedSuccess: boolean;
  cartActionLoading: boolean;
  addToCart: (redirect: boolean) => void;
  removeFromCart: () => void;
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  isOpen,
  setIsOpen,
  selectedProduct,
  getImageSrc,
  cartItem,
  showAddedSuccess,
  cartActionLoading,
  addToCart,
  removeFromCart,
}) => {
  const dispatch = useAppDispatch();
  const shapeOfYouState = useSelector(selectShapeOfYou);
  const [activeTab, setActiveTab] = useState("description");
  const [productDetails, setProductDetails] = useState<any>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [mainSwiper, setMainSwiper] = useState<SwiperType | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [showCertificate, setShowCertificate] = useState(false);

  // Virtual Try On handler
  const openTryOn = () => {
    if (
      window.initMirrarUI &&
      productDetails &&
      process.env.NEXT_PUBLIC_STYLEDOTME_BRAND_ID
    ) {
      window.initMirrarUI(productDetails.sku, {
        brandId: process.env.NEXT_PUBLIC_STYLEDOTME_BRAND_ID,
      });
    }
  };

  // Fetch full product details when selectedProduct changes
  useEffect(() => {
    if (selectedProduct?.slug && isOpen) {
      dispatch(fetchProductForShapeOfYou(selectedProduct.slug))
        .unwrap()
        .then((data) => {
          setProductDetails(data);
        })
        .catch((error) => {
          console.error("Failed to fetch product details:", error);
        });
    }
  }, [selectedProduct?.slug, isOpen, dispatch]);

  // Get the product price info
  const productPrice =
    productDetails?.prices?.[0] || shapeOfYouState.selectedPrice;

  return (
    <div className={styles.modal_pro_desktop_product}>
      <div
        className={`${styles.overlay_desktop} ${isOpen ? styles.show : ""}`}
        onClick={() => setIsOpen(false)}
      >
        <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>
          ✕
        </button>
        <div
          className={`${styles.modal} ${isOpen ? styles.open : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.contentn}>
            <div className={styles.product_details_container}>
              {selectedProduct ? (
                <div className="container">
                  <div className="row">
                    {/* Left Column - Product Images */}
                    <div className="col-md-6 col-xxl-7">
                      <div className={styles.product_details_image}>
                        {/* Product Image Slider */}
                        <div
                          className={styles.imageWrapper_n}
                          style={{ position: "relative" }}
                        >
                          {productDetails?.medias &&
                          productDetails.medias.length > 0 ? (
                            <>
                              {/* Main Image Slider */}
                              <Swiper
                                modules={[Navigation, Thumbs, FreeMode]}
                                navigation={true}
                                onSwiper={setMainSwiper}
                                thumbs={{
                                  swiper:
                                    thumbsSwiper && !thumbsSwiper.destroyed
                                      ? thumbsSwiper
                                      : null,
                                }}
                                onSlideChange={(swiper) =>
                                  setActiveSlide(swiper.activeIndex)
                                }
                                className="product-details-main-swiper"
                                style={{
                                  width: "100%",
                                  height: "500px",
                                  marginBottom: "16px",
                                }}
                              >
                                {productDetails.medias.map(
                                  (media: any, index: number) => (
                                    <SwiperSlide key={index}>
                                      <div
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {media.media_type === "image" ? (
                                          <Image
                                            src={media.media_url}
                                            alt={`${
                                              selectedProduct.title || "Product"
                                            } - ${index + 1}`}
                                            width={500}
                                            height={500}
                                            style={{
                                              maxWidth: "100%",
                                              maxHeight: "100%",
                                              objectFit: "cover",
                                            }}
                                            unoptimized
                                            onError={(e) => {
                                              const target =
                                                e.target as HTMLImageElement;
                                              target.src = jel.src;
                                            }}
                                          />
                                        ) : (
                                          <video
                                            src={media.media_url}
                                            controls
                                            autoPlay
                                            muted
                                            loop
                                            style={{
                                              maxWidth: "100%",
                                              maxHeight: "100%",
                                              objectFit: "cover",
                                            }}
                                          />
                                        )}
                                      </div>
                                    </SwiperSlide>
                                  )
                                )}
                              </Swiper>

                              {/* Thumbnail Slider */}
                              {productDetails.medias.length > 1 && (
                                <Swiper
                                  modules={[Navigation, Thumbs, FreeMode]}
                                  onSwiper={setThumbsSwiper}
                                  slidesPerView={4}
                                  spaceBetween={10}
                                  freeMode={true}
                                  watchSlidesProgress={true}
                                  className="product-details-thumbs-swiper"
                                  style={{
                                    width: "100%",
                                    height: "100px",
                                  }}
                                >
                                  {productDetails.medias.map(
                                    (media: any, index: number) => (
                                      <SwiperSlide
                                        key={index}
                                        onClick={() => {
                                          if (
                                            mainSwiper &&
                                            !mainSwiper.destroyed
                                          ) {
                                            mainSwiper.slideTo(index);
                                            setActiveSlide(index);
                                          }
                                        }}
                                        style={{
                                          cursor: "pointer",
                                          border:
                                            activeSlide === index
                                              ? "1px solid #c91723"
                                              : "1px solid transparent",
                                          borderRadius: "0px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            pointerEvents: "none",
                                          }}
                                        >
                                          <Image
                                            src={
                                              media.media_type === "video"
                                                ? media.preview ||
                                                  media.media_url
                                                : media.media_url
                                            }
                                            alt={`Thumbnail ${index + 1}`}
                                            width={100}
                                            height={100}
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              cursor: "pointer",
                                              pointerEvents: "none",
                                            }}
                                            unoptimized
                                          />
                                        </div>
                                      </SwiperSlide>
                                    )
                                  )}
                                </Swiper>
                              )}

                              {/* Virtual Try On Button - Optional */}
                              {/* {productDetails.try_online && (
                                <div className="w-100 d-flex justify-content-center">
                                  <button
                                    onClick={openTryOn}
                                    style={{
                                      padding: "9px 12px",
                                      marginTop: "16px",
                                      background:
                                        "linear-gradient(90deg, #ff1934 0%, #ea00b8 100%)",
                                      color: "white",
                                      border: "1px solid transparent",
                                      borderRadius: "10px",
                                      fontSize: "16px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "8px",
                                      fontFamily: "Lato, sans-serif",
                                    }}
                                  >
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                    >
                                      <path
                                        d="M12 2L2 7L12 12L22 7L12 2Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M2 17L12 22L22 17"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M2 12L12 17L22 12"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    VIRTUAL TRY ON
                                  </button>
                                </div>
                              )} */}
                            </>
                          ) : (
                            // Fallback to single image if no medias available
                            <Image
                              src={getImageSrc(selectedProduct)}
                              alt={selectedProduct.title || "Product"}
                              width={400}
                              height={400}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = jel.src;
                              }}
                            />
                          )}
                        </div>

                        {/* Product Certificate Desktop */}
                        {/* <div className={styles.product_certificate_desktop}>
                          <div className={styles.certificate_badges}>
                            <span>100% CERTIFIED</span>
                            <span>LIFETIME EXCHANGE @STORE</span>
                          </div>
                        </div> */}
                      </div>
                    </div>

                    {/* Right Column - Product Details */}
                    <div className="col-md-6 col-xxl-5">
                      <div className={styles.product_content}>
                        {/* MADE IN INDIA Badge */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0px",
                            marginBottom: "2px",
                            fontSize: "17px",
                            fontWeight: "800",
                            fontFamily: "newyork",
                            letterSpacing: "1px",
                            color: "#ff1933",
                            animation:
                              "madeInIndiaColor 4s ease-in-out infinite",
                          }}
                        >
                          <span>MADE IN INDIA</span>
                          <Image
                            src={madeInIndiaFlag}
                            alt="Made in India"
                            width={24}
                            height={16}
                            style={{ objectFit: "contain" }}
                          />
                        </div>
                        {/* eslint-disable-next-line react/no-unknown-property */}
                        <style jsx>{`
                          @keyframes madeInIndiaColor {
                            0% {
                              color: #ff1933;
                            }
                            20% {
                              color: #ea00b8;
                            }
                            40% {
                              color: #ff1933;
                            }
                            60% {
                              color: #ea00b8;
                            }
                            80% {
                              color: #ff1933;
                            }
                            100% {
                              color: #ea00b8;
                            }
                          }
                        `}</style>

                        {/* Product Title */}
                        <h2
                          className={styles.title}
                          style={{
                            marginBottom: "2px",
                          }}
                        >
                          {selectedProduct.title ||
                            selectedProduct.slug ||
                            "Product Details"}
                        </h2>

                        {/* SKU */}
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#666",
                            marginBottom: "16px",
                          }}
                        >
                          SKU:{" "}
                          {selectedProduct.sku || productDetails?.sku || "N/A"}
                        </div>

                        {/* Price and Discount Row */}
                        <div className="row">
                          <div className="col-md-12">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <h3
                                className={styles.price}
                                style={{ color: "#ff1933" }}
                              >
                                ₹{" "}
                                {productPrice?.discount?.total ||
                                selectedProduct.price
                                  ? (
                                      productPrice?.discount?.total ||
                                      selectedProduct.price
                                    ).toLocaleString("en-IN")
                                  : "0"}
                              </h3>
                              {productPrice?.discount?.total && (
                                <span
                                  style={{
                                    textDecoration: "line-through",
                                    color: "#999",
                                    fontSize: "16px",
                                    fontWeight: 400,
                                  }}
                                >
                                  ₹{" "}
                                  {(
                                    productPrice?.total || selectedProduct.price
                                  )?.toLocaleString("en-IN")}
                                </span>
                              )}
                            </div>
                            <div className={styles.mrp_text}>
                              (MRP Inclusive of all taxes)
                            </div>
                          </div>
                        </div>

                        {/* Metal and Metal Weight Section */}
                        {(productPrice?.purity ||
                          productPrice?.metal_weight ||
                          productPrice?.gold_weight) && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",

                              border: "1px solid #ea00b8",
                              borderRadius: "8px",
                              marginTop: "16px",
                              marginBottom: "16px",
                              fontFamily: "Lato, sans-serif",
                            }}
                          >
                            {productPrice?.purity && (
                              <div
                                style={{
                                  gap: "16px",
                                  padding: "10px",
                                  borderRight: "1px solid #ea00b8",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "14px",
                                    color: "#666",
                                    marginBottom: "4px",
                                  }}
                                >
                                  Metal
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#262121",
                                  }}
                                >
                                  {productPrice.purity}
                                </div>
                              </div>
                            )}
                            <div
                              style={{
                                gap: "16px",
                                padding: "10px",
                              }}
                            >
                              {(productPrice?.metal_weight ||
                                productPrice?.gold_weight) && (
                                <div
                                  style={{
                                    gap: "16px",
                                    padding: "10px",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      color: "#666",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    Metal Weight
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "600",
                                      color: "#262121",
                                    }}
                                  >
                                    {productPrice.metal_weight ||
                                      productPrice.gold_weight}{" "}
                                    {productPrice.metal_weight ||
                                    productPrice.gold_weight
                                      ? "g"
                                      : ""}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Product Filters (Purity, Size, etc.) - Placeholder */}
                        <div className={styles.product_filters}>
                          {selectedProduct?.purities &&
                            selectedProduct.purities.length > 0 && (
                              <div className={styles.filter_section}>
                                <label>Purity:</label>
                                <div className={styles.filter_options}>
                                  {selectedProduct.purities.map(
                                    (purity: string) => (
                                      <span
                                        key={purity}
                                        className={styles.filter_badge}
                                      >
                                        {purity}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </div>

                        {/* BIS Care App Section */}
                        {/* <div className="row">
                          <div className="col-md-5 text-center">
                            <Link
                              href="https://play.google.com/store/apps/details?id=com.india.senco"
                              passHref
                            >
                              <a
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-100 text-center"
                              >
                                <div className="mx-auto">
                                  <Image
                                    src="https://sencowebfiles.s3.ap-south-1.amazonaws.com/website/files/XIijKO0NEOAb9RKA8cJ9IqPAIUzqtP5SgLcp32Dt.png"
                                    alt="BIS CARE"
                                    width={100}
                                    height={80}
                                    unoptimized
                                    className="mx-auto"
                                  />
                                </div>
                                <p
                                  className="text-center "
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                    marginBottom: "8px",
                                    fontSize: "14px",

                                    fontFamily: "Lato, sans-serif",
                                  }}
                                >
                                  Click here to download the <br /> BIS Care App
                                </p>
                              </a>
                            </Link>
                          </div>
                          <div className="col-md-7">
                            {!!productPrice?.diamond_weight && (
                              <>
                                <div className={styles.sgl}>
                                  <p
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "8px",
                                      marginBottom: "8px",
                                      fontSize: "17px",
                                      fontWeight: "600",
                                      fontFamily: "Lato, sans-serif",
                                    }}
                                  >
                                    <Image
                                      src={sglLogo}
                                      alt="SGL Logo"
                                      width={100}
                                      height={42}
                                      priority
                                    />
                                  </p>
                                </div>
                                <p className="text-center mb-0">
                                  <div className={styles.sgl}>
                                    <span>Certified Diamond Jewellery</span>
                                  </div>
                                </p>
                                <div className={styles.view}>
                                  <p
                                    className="text-center"
                                    style={{
                                      fontSize: "15px",
                                      fontWeight: "600",
                                      marginBottom: "16px",
                                    }}
                                  >
                                    To view sample certificate{" "}
                                    <a
                                      onClick={() => setShowCertificate(true)}
                                      style={{
                                        cursor: "pointer",
                                        color: "#ff1933",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      ( Click Here )
                                    </a>
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </div> */}

                        {/* Delivery Information */}
                        {/* <div className={styles.delivery_section}>
                          <div className={styles.delivery_item}>
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path d="M20 8H4V6H20V8Z" fill="currentColor" />
                            </svg>
                            <span>Free Shipping on all orders</span>
                          </div>
                          {!!selectedProduct?.fastDelivery && (
                            <div className={styles.delivery_item}>
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                                  fill="currentColor"
                                />
                              </svg>
                              <span>Fast Delivery Available</span>
                            </div>
                          )}
                        </div> */}

                        {/* Product Details Tabs */}
                        <div className={styles.product_tabs}>
                          <div className={styles.tabs}>
                            <span
                              className={`${styles.tab} ${
                                activeTab === "description" ? styles.active : ""
                              }`}
                              onClick={() => setActiveTab("description")}
                            >
                              Description
                            </span>
                            <span
                              className={`${styles.tab} ${
                                activeTab === "details" ? styles.active : ""
                              }`}
                              onClick={() => setActiveTab("details")}
                            >
                              Details
                            </span>
                            <span
                              className={`${styles.tab} ${
                                activeTab === "price" ? styles.active : ""
                              }`}
                              onClick={() => setActiveTab("price")}
                            >
                              Price Break-up
                            </span>
                          </div>

                          {/* Tab Content */}
                          <div className={styles.tab_content}>
                            {activeTab === "description" && (
                              <div className={styles.description}>
                                <p>
                                  {selectedProduct?.title ||
                                    "Unveil your inner radiance with this beautiful jewellery piece. A perfect blend of elegance and craftsmanship."}
                                </p>

                                {/* Product Badges */}
                                <div className={styles.product_badges}>
                                  {!!selectedProduct?.fastDelivery && (
                                    <span className={styles.badge_green}>
                                      Fast Delivery
                                    </span>
                                  )}
                                  {!!selectedProduct?.trending && (
                                    <span className={styles.badge_yellow}>
                                      Trending
                                    </span>
                                  )}
                                  {!!selectedProduct?.new_arrival && (
                                    <span className={styles.badge_purple}>
                                      New Arrival
                                    </span>
                                  )}
                                  {!!selectedProduct?.best_seller && (
                                    <span className={styles.badge_blue}>
                                      Best Seller
                                    </span>
                                  )}
                                </div>

                                <p className={styles.disclaimer}>
                                  <strong>Disclaimer:</strong> The dimension of
                                  the actual product might vary. Due to
                                  differences in photographic lighting, the
                                  colour may slightly differ.
                                </p>
                              </div>
                            )}
                            {activeTab === "details" && (
                              <div className={styles.description}>
                                <p className={styles.details_product}>
                                  <strong>Design Code: </strong>
                                  {selectedProduct?.sku || "N/A"}
                                </p>
                                {selectedProduct?.categories &&
                                  selectedProduct.categories.length > 0 && (
                                    <p className={styles.details_product}>
                                      <strong>Category: </strong>
                                      {selectedProduct.categories
                                        .map((cat: any) => cat.name)
                                        .join(", ")}
                                    </p>
                                  )}
                              </div>
                            )}
                            {activeTab === "price" && (
                              <div className={styles.description}>
                                <p className={styles.price_brackup}>
                                  <strong>Price: </strong>
                                  <span style={{ color: "#ff1933" }}>
                                    ₹
                                    {productPrice?.discount?.total ||
                                    selectedProduct?.price
                                      ? (
                                          productPrice?.discount?.total ||
                                          selectedProduct?.price
                                        ).toLocaleString("en-IN")
                                      : "0"}
                                  </span>
                                  {productPrice?.discount?.total && (
                                    <span
                                      style={{
                                        textDecoration: "line-through",
                                        color: "#999",
                                        marginLeft: "8px",
                                        fontSize: "14px",
                                      }}
                                    >
                                      ₹
                                      {(
                                        productPrice?.total ||
                                        selectedProduct?.price
                                      )?.toLocaleString("en-IN")}
                                    </span>
                                  )}
                                </p>
                                {productPrice?.discount?.total &&
                                  productPrice?.total && (
                                    <p className={styles.price_brackup}>
                                      <strong>You Save: </strong>
                                      <span
                                        style={{
                                          color: "#28a745",
                                          fontWeight: 600,
                                        }}
                                      >
                                        ₹
                                        {(
                                          productPrice.total -
                                          productPrice.discount.total
                                        ).toLocaleString("en-IN")}
                                      </span>
                                    </p>
                                  )}
                                {typeof selectedProduct?.stock !==
                                  "undefined" && (
                                  <p className={styles.price_brackup}>
                                    <strong>Stock Status: </strong>
                                    <span
                                      style={{
                                        color:
                                          selectedProduct.stock > 0
                                            ? "#28a745"
                                            : "#dc3545",
                                      }}
                                    >
                                      {selectedProduct.stock > 0
                                        ? "In Stock"
                                        : "Made To Order"}
                                    </span>
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Add to Cart Button */}
                        <div
                          style={{ marginTop: "24px", marginBottom: "24px" }}
                        >
                          {showAddedSuccess ? (
                            <button
                              disabled={true}
                              style={{
                                width: "260px",
                                padding: "10px 24px",
                                backgroundColor: "#28a745",
                                color: "white",
                                border: "none",
                                borderRadius: "30px",
                                fontSize: "16px",
                                fontWeight: "600",
                                cursor: "not-allowed",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                float: "right",
                              }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M20 6L9 17L4 12"
                                  stroke="white"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              Added Successfully
                            </button>
                          ) : cartItem ? (
                            <button
                              disabled
                              style={{
                                width: "260px",
                                padding: "10px 24px",
                                backgroundColor: "#22c55e",
                                color: "white",
                                border: "none",
                                borderRadius: "30px",
                                fontSize: "16px",
                                fontWeight: "600",
                                cursor: "default",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                transition: "background-color 0.3s ease",
                                float: "right",
                              }}
                            >
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "8px",
                                }}
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M20 6L9 17L4 12"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Added To Cart
                              </span>
                            </button>
                          ) : (
                            <button
                              onClick={() => addToCart(false)}
                              disabled={cartActionLoading || !selectedProduct}
                              style={{
                                width: "260px",
                                padding: "10px 24px",
                                backgroundColor:
                                  cartActionLoading || !selectedProduct
                                    ? "#999"
                                    : "#c91723",
                                color: "white",
                                border: "none",
                                borderRadius: "30px",
                                fontSize: "16px",
                                fontWeight: "600",
                                cursor:
                                  cartActionLoading || !selectedProduct
                                    ? "not-allowed"
                                    : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                transition: "background-color 0.3s ease",
                                float: "right",
                              }}
                              onMouseEnter={(e) => {
                                if (!cartActionLoading && selectedProduct) {
                                  e.currentTarget.style.backgroundColor =
                                    "#a01219";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!cartActionLoading && selectedProduct) {
                                  e.currentTarget.style.backgroundColor =
                                    "#c91723";
                                }
                              }}
                            >
                              {cartActionLoading ? (
                                <>
                                  <Spinner animation="border" size="sm" />
                                  Adding...
                                </>
                              ) : (
                                <>
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M4 19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21C6.53043 21 7.03914 20.7893 7.41421 20.4142C7.78929 20.0391 8 19.5304 8 19C8 18.4696 7.78929 17.9609 7.41421 17.5858C7.03914 17.2107 6.53043 17 6 17C5.46957 17 4.96086 17.2107 4.58579 17.5858C4.21071 17.9609 4 18.4696 4 19Z"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M12.5 17H6V3H4"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M6 5L20 6L19.14 12.017M16.5 13H6"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M16 19H22"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M19 16V22"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  Add to Cart
                                </>
                              )}
                            </button>
                          )}
                        </div>

                        {/* Product Certificate Mobile */}
                        <div className={styles.product_certificate_mobile}>
                          <div className={styles.certificate_badges}>
                            <span>100% CERTIFIED</span>
                            <span>LIFETIME EXCHANGE @STORE</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  <p>No product selected</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Certificate Modal */}
      <Modal
        show={showCertificate}
        onHide={() => setShowCertificate(false)}
        centered
        size="lg"
      >
        <Modal.Body style={{ padding: 0 }}>
          <Image
            src={require("@/assets/images/SGL-CRT.jpg")}
            alt="Sample Certificate"
            priority
            style={{ width: "100%", height: "auto" }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ProductDetailsModal;
